import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, FormControl, FormLabel, Grid, MenuItem, OutlinedInput, SelectChangeEvent, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import { useSnackbar } from "notistack";

import analytics from "fond/analytics";
import { useGetProjectQuery, useUpdateProjectMutation } from "fond/api";
import { CURRENCY_OPTIONS, SYSTEM_OF_MEASUREMENT_OPTIONS } from "fond/form/fields/optionSets";
import { useGetScenarioConfiguration } from "fond/hooks/useGetScenarioConfiguration";
import { usePermissionCheck } from "fond/hooks/usePermissionCheck";
import { getCurrentProject } from "fond/project";
import { ProjectAction } from "fond/project/reduxActions";
import { Store, SystemsOfMeasurement } from "fond/types";
import { Currency } from "fond/types/currency";
import { Actions } from "fond/utils/permissions";
import { isCollaborationProject } from "fond/utils/project";
import { Modal } from "fond/widgets";

import { CurrencySelect, SelectHelperText, ShortSelect } from "./projectSettings.styles";

interface ProjectSettingsModalProps {
  onClose: () => void;
}

const ProjectSettingsModal: React.FC<ProjectSettingsModalProps> = ({ onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const projectId = useSelector((state: Store) => state.project.projectId);
  const { data: project } = useGetProjectQuery(projectId);
  const [systemOfMeasurement, setSystemOfMeasurement] = useState(project?.SystemOfMeasurement);
  const [currency, setCurrency] = useState(project?.Currency);
  const [updateProject] = useUpdateProjectMutation();
  const currentProject = useSelector((state: Store) => getCurrentProject(state.project));
  const { hasGeneratedScenario: hasGeneratedCollaborationScenario } = useGetScenarioConfiguration({ projectId });

  const hasEditPermission = usePermissionCheck(
    isCollaborationProject(currentProject) ? Actions.PROJECT_EDIT : Actions.PLANNER_PROJECT_EDIT,
    currentProject?.Permission.Level
  );
  // Once a collaboration CTS scenario has been generated, the Cost to Serve setup becomes read-only.
  // Project settings must be locked down the same way, otherwise a user could change the Currency/Unit
  // type here after generation, bypassing that restriction.
  const canEditSettings = hasEditPermission && !hasGeneratedCollaborationScenario;

  const handleOnSave = async () => {
    if (!project) {
      return;
    }

    const hasSystemOfMeasurementChanged = !!systemOfMeasurement && systemOfMeasurement !== project.SystemOfMeasurement;
    const hasCurrencyChanged = !!currency && currency !== project.Currency;

    if (!hasSystemOfMeasurementChanged && !hasCurrencyChanged) {
      onClose();
      return;
    }

    try {
      await updateProject({
        ID: project.ID,
        ...(hasSystemOfMeasurementChanged && { SystemOfMeasurement: systemOfMeasurement }),
        ...(hasCurrencyChanged && { Currency: currency }),
      }).unwrap();

      if (hasSystemOfMeasurementChanged) {
        dispatch({
          type: ProjectAction.UPDATE_SYSTEM_OF_MEASUREMENT.SUCCESS,
          data: { ID: project.ID, SystemOfMeasurement: systemOfMeasurement },
        });
        analytics.track("Update system of measurement", { projectId, systemOfMeasurement, source: "project-settings" });
      }
      if (hasCurrencyChanged) {
        dispatch({
          type: ProjectAction.UPDATE_CURRENCY.SUCCESS,
          data: { ID: project.ID, Currency: currency },
        });
        analytics.track("Update currency", { projectId, currency, source: "project-settings" });
      }

      enqueueSnackbar("Project settings updated.");
      onClose();
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const handleSystemOfMeasurementChange = (e: SelectChangeEvent<`${SystemsOfMeasurement}`>) => {
    setSystemOfMeasurement(e.target.value as `${SystemsOfMeasurement}`); // Mui select value does not get type narrowed with generics
  };

  const handleCurrencyChange = (e: SelectChangeEvent<Currency>) => {
    setCurrency(e.target.value as Currency); // Mui select value does not get type narrowed with generics
  };

  const handleOnClose = () => {
    analytics.track("Closed project setting modal");
    onClose();
  };

  return (
    <Modal
      open
      header="Project settings"
      onClose={handleOnClose}
      data-testid="project-settings"
      content={
        <Grid container direction="column">
          <Typography>General options for the current project.</Typography>
          <Box mt={2}>
            <FormControl fullWidth>
              <FormLabel>Currency</FormLabel>
              <CurrencySelect
                value={currency}
                data-testid="currency-input"
                onChange={handleCurrencyChange}
                input={<OutlinedInput size="small" />}
                margin="dense"
                disabled={!canEditSettings}
              >
                {CURRENCY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.displayValue}
                  </MenuItem>
                ))}
              </CurrencySelect>
              <SelectHelperText>
                Changes only the currency symbol displayed within this project. It is not used for any currency conversion.
              </SelectHelperText>
            </FormControl>
          </Box>
          <Box mt={2}>
            <FormControl fullWidth>
              <FormLabel>Unit type</FormLabel>
              <ShortSelect
                value={systemOfMeasurement}
                data-testid="measurement-input"
                onChange={handleSystemOfMeasurementChange}
                input={<OutlinedInput size="small" />}
                margin="dense"
                disabled={!canEditSettings}
              >
                {SYSTEM_OF_MEASUREMENT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.displayValue}
                  </MenuItem>
                ))}
              </ShortSelect>
              <SelectHelperText>Determines the unit of measurements the project will use.</SelectHelperText>
            </FormControl>
          </Box>
        </Grid>
      }
      actions={
        <>
          <Button data-testid="close-button" onClick={handleOnClose}>
            Close
          </Button>
          <Button data-testid="save-button" variant="contained" disabled={!canEditSettings} color="primary" onClick={handleOnSave}>
            Update Settings
          </Button>
        </>
      }
    />
  );
};

interface ProjectSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

const ProjectSettings: React.FC<ProjectSettingsProps> = ({ isOpen, onClose }) => {
  // We unmount the modal when the settings are closed to reset internal state - this prevents stale data
  // from being shown if the modal is reopened after an external change is made.
  if (!isOpen) {
    return null;
  }

  return <ProjectSettingsModal onClose={onClose} />;
};

export default ProjectSettings;
